import { mediaGcd, mediaInteger as integer, mediaObject as object, normalizeMediaInspectionConfig,
    type MarketMediaInspectionConfig, type MediaRatio, type MediaInspectionFilters } from './mediaInspectionConfig';
import { publicMediaSource } from './mediaResults';

export interface MediaGeometry {
    schema_version: 'market-media-geometry/v1'; basis: 'image_frame' | 'video_stream' | 'no_video';
    status: 'known' | 'unknown' | 'not_applicable'; reason: null | 'orientation_unknown' | 'frame_metadata_unavailable' | 'pixel_aspect_unknown' | 'no_video_stream';
    rotation_degrees: 0 | 90 | 180 | 270 | null; sample_aspect_ratio: MediaRatio | null;
    oriented_width: number | null; oriented_height: number | null; display_aspect_ratio: MediaRatio | null;
}
export interface MediaFacts {
    schema_version: 'market-media-facts/v1'; kind: 'image' | 'video' | 'audio'; duration_ms: number | null;
    encoded_width: number | null; encoded_height: number | null; geometry: MediaGeometry | null;
}
export type MediaSelectionState = 'matched' | 'excluded' | 'unknown';
export interface MediaSelection { schema_version: 'market-media-selection/v1'; state: MediaSelectionState; reasons: string[] }
export interface MediaInspectionItem {
    ordinal: number; job_id: string; evidence_id: string; state: MediaSelectionState | 'pending' | 'unavailable';
    source_url: string | null; facts: MediaFacts | null; selection: MediaSelection | null;
}
export interface MarketMediaInspectionResults {
    schema_version: 'market-media-inspection-results/v1'; analysis_version: string; content_trust: 'external_untrusted';
    state: 'disabled' | 'not_started' | 'planning' | 'queued' | 'ready' | 'partial' | 'unavailable';
    config: MarketMediaInspectionConfig | null; records_considered: number; records_without_media: number; assets_planned: number;
    counts: Record<MediaInspectionItem['state'], number>; items: MediaInspectionItem[]; next_cursor: string | null;
    coverage: 'bounded_sample_not_complete'; execution_enabled: boolean;
}
const uuid = (value: unknown): value is string => typeof value === 'string' && /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value);
const nullableInteger = (v: unknown, max: number) => v === null || integer(v, 1, max);
const ratio = (v: unknown, max: number): v is MediaRatio => object(v) && Object.keys(v).length === 2
    && integer(v.numerator, 1, max) && integer(v.denominator, 1, max) && mediaGcd(v.numerator, v.denominator) === 1;
const invalid = () => new Error('Invalid stored media inspection results');

function facts(value: unknown): MediaFacts {
    if (!object(value) || Object.keys(value).length !== 6 || value.schema_version !== 'market-media-facts/v1'
        || !['image', 'video', 'audio'].includes(String(value.kind)) || !nullableInteger(value.duration_ms, 600_000)
        || !nullableInteger(value.encoded_width, 8192) || !nullableInteger(value.encoded_height, 8192)
        || (value.encoded_width === null) !== (value.encoded_height === null)
        || (value.kind === 'audio') !== (value.encoded_width === null)
        || value.kind === 'image' && value.duration_ms !== null) throw invalid();
    const g = value.geometry;
    if (g !== null) {
        if (!object(g) || Object.keys(g).length !== 9 || g.schema_version !== 'market-media-geometry/v1'
            || g.basis !== (value.kind === 'image' ? 'image_frame' : value.kind === 'audio' ? 'no_video' : 'video_stream')
            || !['known', 'unknown', 'not_applicable'].includes(String(g.status))
            || ![null, 'orientation_unknown', 'frame_metadata_unavailable', 'pixel_aspect_unknown', 'no_video_stream'].includes(g.reason as any)
            || ![null, 0, 90, 180, 270].includes(g.rotation_degrees as any)
            || !(g.sample_aspect_ratio === null || ratio(g.sample_aspect_ratio, 1_000_000))
            || !(g.display_aspect_ratio === null || ratio(g.display_aspect_ratio, 8_192_000_000))
            || !nullableInteger(g.oriented_width, 8192) || !nullableInteger(g.oriented_height, 8192)) throw invalid();
        if (value.kind === 'audio') {
            if (g.status !== 'not_applicable' || g.reason !== 'no_video_stream'
                || [g.rotation_degrees, g.sample_aspect_ratio, g.oriented_width, g.oriented_height, g.display_aspect_ratio].some(v => v !== null)) throw invalid();
        } else if (g.rotation_degrees === null) {
            if (g.status !== 'unknown' || !['orientation_unknown', 'frame_metadata_unavailable'].includes(String(g.reason))
                || g.reason === 'frame_metadata_unavailable' && g.basis !== 'image_frame'
                || [g.oriented_width, g.oriented_height, g.display_aspect_ratio].some(v => v !== null)) throw invalid();
        } else {
            const swapped = g.rotation_degrees === 90 || g.rotation_degrees === 270;
            if (g.oriented_width !== (swapped ? value.encoded_height : value.encoded_width)
                || g.oriented_height !== (swapped ? value.encoded_width : value.encoded_height)) throw invalid();
            if (g.sample_aspect_ratio === null) {
                if (g.status !== 'unknown' || g.reason !== 'pixel_aspect_unknown' || g.display_aspect_ratio !== null) throw invalid();
            } else {
                if (g.status !== 'known' || g.reason !== null || !ratio(g.display_aspect_ratio, 8_192_000_000)) throw invalid();
                const sar = g.sample_aspect_ratio as MediaRatio;
                const n = Number(value.encoded_width) * sar.numerator, d = Number(value.encoded_height) * sar.denominator, gcd = mediaGcd(n, d);
                if (g.display_aspect_ratio.numerator !== (swapped ? d : n) / gcd
                    || g.display_aspect_ratio.denominator !== (swapped ? n : d) / gcd) throw invalid();
            }
        }
    }
    return value as unknown as MediaFacts;
}

/** Validate the stored selection against public facts; never estimate missing dimensions. */
function selection(filters: MediaInspectionFilters, facts: MediaFacts): MediaSelection {
    const excluded: string[] = [], unknown: string[] = [];
    const threshold = (fact: number | null | undefined, bound: number | null, minimum: boolean, field: string) => {
        if (bound === null) return;
        if (fact == null) unknown.push(field + '_unknown');
        else if (minimum ? fact < bound : fact > bound) excluded.push(field + (minimum ? '_below_min' : '_above_max'));
    };
    if (facts.kind !== 'image') {
        threshold(facts.duration_ms, filters.min_duration_ms, true, 'duration'); threshold(facts.duration_ms, filters.max_duration_ms, false, 'duration');
    }
    threshold(facts.geometry?.oriented_width, filters.min_width, true, 'width');
    threshold(facts.geometry?.oriented_height, filters.min_height, true, 'height');
    if (filters.aspect_ratios.length) {
        const actual = facts.geometry?.display_aspect_ratio;
        if (!actual) unknown.push('aspect_ratio_unknown');
        else if (!filters.aspect_ratios.some(r => r.numerator * actual.denominator === r.denominator * actual.numerator)) excluded.push('aspect_ratio_excluded');
    }
    return { schema_version: 'market-media-selection/v1', state: excluded.length ? 'excluded' : unknown.length ? 'unknown' : 'matched',
        reasons: [...new Set(excluded.length ? excluded : unknown)] };
}

export function validateMediaInspectionResults(value: unknown, version: string, cursor: string | null): MarketMediaInspectionResults {
    const after = cursor === null ? -1 : Number(cursor);
    if (!integer(after, -1, 99) || cursor !== null && !/^(0|[1-9]\d?)$/.test(cursor)
        || !object(value) || Object.keys(value).length !== 13 || value.schema_version !== 'market-media-inspection-results/v1'
        || !uuid(version) || value.analysis_version !== version || value.content_trust !== 'external_untrusted'
        || value.coverage !== 'bounded_sample_not_complete' || typeof value.execution_enabled !== 'boolean'
        || !['disabled', 'not_started', 'planning', 'queued', 'ready', 'partial', 'unavailable'].includes(String(value.state))) throw invalid();
    const config = value.config === null ? null : normalizeMediaInspectionConfig(value.config);
    if ((value.state === 'disabled') !== !config?.enabled || !integer(value.records_considered, 0, config?.enabled ? config.max_records : 0)
        || !integer(value.records_without_media, 0, value.records_considered) || !integer(value.assets_planned, 0, config?.enabled ? config.max_assets : 0)
        || !object(value.counts) || Object.keys(value.counts).length !== 5 || !Array.isArray(value.items)
        || value.items.length !== Math.min(20, Math.max(0, value.assets_planned - after - 1))) throw invalid();
    const states = ['matched', 'excluded', 'unknown', 'unavailable', 'pending'] as const;
    if (!states.every(key => integer((value.counts as Record<string, unknown>)[key], 0, value.assets_planned as number))
        || Object.values(value.counts).reduce<number>((sum, amount) => sum + Number(amount), 0) !== value.assets_planned) throw invalid();
    const jobs = new Set<string>(), observed = { matched: 0, excluded: 0, unknown: 0, unavailable: 0, pending: 0 };
    let ordinal = after;
    for (const item of value.items) {
        if (!object(item) || Object.keys(item).length !== 7 || item.ordinal !== ++ordinal || !uuid(item.job_id) || jobs.has(item.job_id)
            || !uuid(item.evidence_id) || !states.includes(item.state as any) || !publicMediaSource(item.source_url)) throw invalid();
        jobs.add(item.job_id); observed[item.state as keyof typeof observed]++;
        if (item.state === 'pending' || item.state === 'unavailable') {
            if (item.facts !== null || item.selection !== null) throw invalid();
        } else {
            if (!config?.enabled) throw invalid();
            const expected = selection(config.filters, facts(item.facts));
            if (!object(item.selection) || Object.keys(item.selection).length !== 3 || item.selection.schema_version !== expected.schema_version
                || item.selection.state !== item.state || item.state !== expected.state
                || JSON.stringify(item.selection.reasons) !== JSON.stringify(expected.reasons)) throw invalid();
        }
    }
    if (states.some(key => observed[key] > (value.counts as Record<string, number>)[key])
        || value.next_cursor !== (ordinal < value.assets_planned - 1 ? String(ordinal) : null)) throw invalid();
    return value as unknown as MarketMediaInspectionResults;
}
